import { TeachersFilters } from '../../interfaces/EstudiApiClient/dtos/TeachersFilters';
import { TeachersUIFilters } from '../../../types/TeachersUIFilters';
import { TeacherBusinessHours } from '../../../types/TeacherBusinessHours';
import { DayOfWeek } from '../../../types/DayOfWeek';
import { TimeRange } from '../../../types/TimeRange';
import { Availability } from '../../../types/Availability';
import { InstitutionTypeFilter } from '../queries/institutionsQueryFunction';

const TIME_RANGE_WINDOWS: Record<TimeRange, { start: string; end: string }> = {
  [TimeRange.MORNING]: { start: '08:00:00-03:00', end: '12:00:00-03:00' },
  [TimeRange.AFTERNOON]: { start: '13:00:00-03:00', end: '17:00:00-03:00' },
  [TimeRange.EVENING]: { start: '18:00:00-03:00', end: '22:00:00-03:00' },
  [TimeRange.ALL_DAY]: { start: '08:00:00-03:00', end: '22:00:00-03:00' }
};

function getNextDatForDayOfWeek(base: Date, dayOfWeek: DayOfWeek, weekOffset: number): Date {
  const result = new Date(base);
  const currentDay = result.getDay();
  const daysUntil = ((dayOfWeek + 7 - currentDay) % 7) + weekOffset * 7;

  result.setDate(result.getDate() + daysUntil);

  return result;
}

function formatIsoDateTime(date: Date, time: string): string {
  const [hh, mm, rest] = time.split(':');
  const [ss, zone] = rest.split('-');
  const dateStr = date.toISOString().split('T')[0];

  return `${dateStr}T${hh}:${mm}:${ss}-${zone}`;
}

function calculateListOfHours(
  dayOfWeeks: DayOfWeek[],
  timeRanges: TimeRange[],
  numberOfWeeks: number
): TeacherBusinessHours[] {
  const now = new Date();
  const result: TeacherBusinessHours[] = [];

  for (let week = 0; week < numberOfWeeks; week++) {
    dayOfWeeks.forEach((day) => {
      const targetDate = getNextDatForDayOfWeek(now, day, week);

      for (const timeRange of timeRanges) {
        const { start, end } = TIME_RANGE_WINDOWS[timeRange];

        result.push({
          startTime: formatIsoDateTime(targetDate, start),
          endTime: formatIsoDateTime(targetDate, end)
        });
      }
    });
  }

  return result;
}

function getWeeksToProject(availability?: Availability): number {
  switch (availability) {
    case Availability.NEXT_48_HOURS:
      return 0; // It's handled separately
    case Availability.THIS_WEEK:
      return 1;
    case Availability.NEXT_2_WEEKS:
      return 2;
    case Availability.NEXT_30_DAYS:
      return 4;
    default:
      return 1;
  }
}

function generateNext48hSlot(): TeacherBusinessHours[] {
  const now = new Date();
  const end = new Date(now.getTime() + 48 * 60 * 60 * 1000);

  return [{ startTime: now.toISOString(), endTime: end.toISOString() }];
}

export function mapFiltersToBackendQuery(
  uiFilters: TeachersUIFilters,
  courseId: string,
  type?: InstitutionTypeFilter
): TeachersFilters {
  const backFilters: TeachersFilters = { courseId };

  if (type && type !== 'all') {
    backFilters.type = type;
  }

  if (uiFilters.minPricePerHour) {
    backFilters.minPricePerHour = uiFilters.minPricePerHour;
  }

  if (uiFilters.maxPricePerHour) {
    backFilters.maxPricePerHour = uiFilters.maxPricePerHour;
  }

  if (uiFilters.modality) {
    backFilters.modality = uiFilters.modality;
  }

  if (uiFilters.availability === Availability.NEXT_48_HOURS) {
    backFilters.availableIn = generateNext48hSlot();
  } else if (uiFilters.daysOfWeek && uiFilters.daysOfWeek.length > 0) {
    const weeks = getWeeksToProject(uiFilters.availability);

    // FixMe: The timeRanges filter is not apply unless daysOfWeek was set.
    backFilters.availableIn = calculateListOfHours(
      uiFilters.daysOfWeek,
      uiFilters.timeRanges ?? [TimeRange.ALL_DAY],
      weeks
    );
  }

  return backFilters;
}
